import { useState, useEffect } from 'react';
import { Modal, Form, DatePicker, Tag, Button } from '@bancobarigui/barihaus';
import { message, Input, Checkbox, TimePicker, Space } from 'antd';
import { videoSessionService, ScheduleVideoSessionRequest } from '@/services/videoSession.service';
import dayjs from 'dayjs';

interface VideoSessionScheduleModalProps {
  open: boolean;
  inspectionItemId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function VideoSessionScheduleModal({
  open,
  inspectionItemId,
  onClose,
  onSuccess,
}: VideoSessionScheduleModalProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [participantEmails, setParticipantEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState<string>('');

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({
        requiresConfirmation: true,
      });
      setParticipantEmails([]);
      setEmailInput('');
    }
  }, [open, form]);


  const handleAddEmail = () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) {
      message.warning('Digite um email válido');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      message.error('Email inválido');
      return;
    }

    if (participantEmails.includes(email)) {
      message.warning('Este email já foi adicionado');
      return;
    }

    setParticipantEmails([...participantEmails, email]);
    setEmailInput('');
    message.success(`Email ${email} adicionado`);
  };

  const handleRemoveEmail = (email: string) => {
    setParticipantEmails(participantEmails.filter(e => e !== email));
  };


  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const request: ScheduleVideoSessionRequest = {
        inspectionItemId,
        scheduleDate: values.scheduleDate.format('YYYY-MM-DD'),
        startTime: values.startTime ? values.startTime.format('HH:mm') : undefined,
        notes: values.notes,
        title: values.title,
        description: values.description,
        maxParticipants: values.maxParticipants,
        requiresConfirmation: values.requiresConfirmation !== false,
        ...(participantEmails.length > 0 && { participantEmails }),
      };

      await videoSessionService.scheduleVideoSession(request);
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Erro ao agendar videoconferência');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="Agendar Videoconferência"
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={600}
      okText="Agendar"
      cancelText="Cancelar"
      bodyStyle={{
        maxHeight: 'calc(100vh - 250px)',
        overflowY: 'auto',
        paddingRight: '8px'
      }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          requiresConfirmation: true,
        }}
      >
        <Form.Item
          name="scheduleDate"
          label="Data do Agendamento"
          rules={[{ required: true, message: 'Selecione a data do agendamento' }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            disabledDate={(current) => current && current < dayjs().startOf('day')}
            format="DD/MM/YYYY"
          />
        </Form.Item>

        <Form.Item
          name="startTime"
          label="Horário de Início"
          rules={[{ required: true, message: 'Selecione o horário de início' }]}
        >
          <TimePicker
            placeholder="Selecione o horário"
            format="HH:mm"
            style={{ width: '100%' }}
            minuteStep={15}
          />
        </Form.Item>

        <Form.Item name="description" label="Descrição">
          <Input.TextArea
            rows={3}
            placeholder="Descrição opcional da videoconferência"
          />
        </Form.Item>

        <Form.Item name="maxParticipants" label="Máximo de Participantes">
          <Input type="number" min={1} placeholder="Deixe em branco para ilimitado" />
        </Form.Item>

        <Form.Item name="requiresConfirmation" valuePropName="checked">
          <Checkbox>Requer confirmação dos participantes</Checkbox>
        </Form.Item>

        <Form.Item label="Participantes">
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
              Adicionar participantes por email:
            </div>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="Digite o email do participante"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onPressEnter={handleAddEmail}
                style={{ flex: 1 }}
              />
              <Button buttonColor="primary" onClick={handleAddEmail}>
                Adicionar
              </Button>
            </Space.Compact>
          </div>

          {participantEmails.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
                Participantes adicionados ({participantEmails.length}):
              </div>
              {participantEmails.map((email) => (
                <Tag
                  key={email}
                  closable
                  onClose={() => handleRemoveEmail(email)}
                  style={{ marginBottom: 4, marginRight: 4 }}
                  color="blue"
                >
                  {email}
                </Tag>
              ))}
            </div>
          )}
        </Form.Item>

        <Form.Item name="notes" label="Observações">
          <Input.TextArea rows={2} placeholder="Observações adicionais" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

